import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { addCrossSell, addPromoCode, addSubscription, addToCart, checkRedemptionCodeValidity, emptyCart, forgetCart, loadCart, modifyCart, modifyGiftCards, modifyLineItem, purchasePrepaidSubscription, redeemGiftCard, redeemPrepaidSubscription, removeFromCart, removePromoCode } from '../redux/actions';
/**
 * The `useCart` hook returns information about the current shopping cart, and
 * functions for creating and updating the shopping cart.
 *
 * @example
 *
 * ```javascript
 * import { useCart } from '@chordcommerce/react-autonomy'
 *
 * // inside a function component
 * const { cart, addToCart } = useCart()
 * ```
 *
 * @category Hook
 */
export function useCart() {
  const cart = useSelector(state => state.cart, shallowEqual);
  const {
    isFetching,
    isLoaded,
    error
  } = cart;
  const dispatch = useDispatch();
  const actionDispatches = useMemo(() => bindActionCreators({
    addCrossSell,
    addPromoCode,
    addSubscription,
    addToCart,
    checkRedemptionCodeValidity,
    emptyCart,
    forgetCart,
    loadCart,
    modifyCart,
    modifyGiftCards,
    modifyLineItem,
    purchasePrepaidSubscription,
    redeemGiftCard,
    redeemPrepaidSubscription,
    removeFromCart,
    removePromoCode
  }, dispatch), [dispatch]);
  useEffect(() => {
    if (isFetching || isLoaded || error) return;
    if (Object.keys(cart.data).length === 0) {
      actionDispatches.loadCart();
    }
  }, [cart]);
  return {
    cart: cart.data,
    promoCode: cart.promoCode,
    isFetching,
    isLoaded,
    error,
    ...actionDispatches
  };
}